import {
  draggable,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  ButtonSmallSecondary,
  ButtonSmallSubtleIcon,
  GenericBadgeV2,
  GenericInput,
  Icon,
  IconSVG,
  InputIconPosition,
  InputMode,
  MicroButton,
  SVGColorKey,
} from "@studio3t/generic-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  defaultRangeExtractor,
  type Range,
  useVirtualizer,
} from "@tanstack/react-virtual";
import type { OverlayScrollbarsComponentRef } from "overlayscrollbars-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { type DefaultTheme, useTheme } from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { StageTag } from "@/components/PipelineFlowCell";
import { SkeletonBar } from "@/components/Skeleton";
import { setDragPreviewChip } from "./dragPreviewChip";
import { useExplorer } from "./ExplorerContext";
import type { ExplorerItem, ExplorerSection } from "./explorerData";
import {
  explorerLeafStageTarget,
  leafActivationProps,
} from "./explorerKeyboard";
import { refreshExplorer } from "./explorerRefresh";
import {
  type ExplorerRow,
  flattenExplorerRows,
  stickyRowsAt,
} from "./explorerRows";
import {
  explorerEmptyHint,
  visibleExplorerSections,
} from "./explorerTabSections";
import {
  EXPLORER_RESOURCE_DRAG_KIND,
  type ExplorerResourceDragData,
  isExplorerResourceDragData,
} from "./PipelineNavBar";
import {
  canPreviewSample,
  canPreviewScript,
  explorerDragAllowed,
  isSuiteRowId,
  parseSuiteRowId,
  usePipelineStaging,
} from "./pipelineStaging";
import {
  acquirePreviewSuppression,
  releasePreviewSuppression,
  setPrimaryButtonHeld,
  suppressPreviewsWhileMenuOpen,
} from "./previewMenuPin";
import { SchemaRowContextMenu } from "./SchemaDelete";
import {
  closeOpenPreview,
  type PreviewKind,
  usePreviewScrollGuard,
  useResourceHoverPreview,
} from "./SchemaPreview";
import { useStudioPipeline } from "./StudioPipelineProvider";
import { CollapseSectionIcon, ExpandSectionIcon } from "./sectionIcons";
import { parseSchemaRowId, SOURCE_EDITOR_ID } from "./stagingModel";
import { SuiteRowContextMenu } from "./TestPreview";

const SIDEBAR = designVariables.ui.sidebar;
const ITEM = SIDEBAR.list.item;
const SEMANTIC = designVariables.ui.semantic;
const CARDS = designVariables.ui.cards;

// Figma: Bridge_Explorer (13123-100623) expanded / Content panel (13140-85610)
// collapsed. Data and persisted UI state live in explorerData.ts; SRC/SINK/MW
// badges come later.

// Pulsing placeholder rows shown while a tree level is loading.
function SkeletonTreeRows({ rows, indent }: { rows: number; indent: number }) {
  const widths = ["55%", "40%", "62%", "35%", "48%", "58%"];
  return (
    <>
      {Array.from({ length: rows }, (_, i) => `skeleton-${i}`).map((key, i) => (
        <SkeletonRow key={key} $indent={indent}>
          <SkeletonBar $width="20px" $height="20px" />
          <SkeletonBar $width={widths[i % widths.length]} />
        </SkeletonRow>
      ))}
    </>
  );
}

// The row-kind and staging locks the drag hook layers on top of
// explorerDragAllowed's view/tab gate: a view-only studio drags nothing,
// only payload-carrying rows drag, and a staged resource drags no more —
// except schemas, which stay draggable while staged.
function explorerRowDraggable(
  item: ExplorerItem,
  flags: {
    isSchema: boolean;
    isTest: boolean;
    staged: boolean;
    editsLocked: boolean;
  },
  target: Parameters<typeof explorerDragAllowed>[0],
  sources: Parameters<typeof explorerDragAllowed>[2],
): boolean {
  const { isSchema, isTest, staged, editsLocked } = flags;
  if (editsLocked) return false;
  // Only leaf resources, schema leaves and suite rows carry a payload.
  const carriesPayload = item.id.startsWith("resource:") || isSchema || isTest;
  if (!carriesPayload) return false;
  const stagingLocked = staged && !isSchema;
  if (stagingLocked) return false;
  return explorerDragAllowed(
    target,
    { provider: item.provider, isSchema, isTest },
    sources,
  );
}

// Leaf resources can be dragged onto the drop zones of the stage tab on
// screen. Only rows with somewhere to land are draggable
// (explorerDragAllowed): the tab's own kind per the staging rules
// (canStageAs — udf is transform-only, http_source is source-only; SOURCE
// only while its slot is empty), schemas on every stage tab, suites on the
// Tests tab, nothing in the Full pipeline view. A resource already staged
// (badged, listed in the nav bar) can't be dragged again until it's
// removed — for a suite row that means it IS the staged suite. A view-only
// studio (editsLocked) drags nothing. Non-eligible rows get an inert ref.
// The row dims while its drag is in flight.
function useExplorerResourceDrag(item: ExplorerItem) {
  const theme = useTheme();
  const { roleOf, stagedSuite, studioView, stageTab, sources } =
    usePipelineStaging();
  const { editsLocked } = useStudioPipeline();
  const ref = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  const { id, name, icon, provider } = item;
  const isSchema = id.startsWith("schema:");
  const isTest = isSuiteRowId(id);
  // Schemas stay draggable while staged — one schema can attach to any
  // number of stages, across both stage tabs. Other resources leave the
  // pool once staged. The panel holds exactly ONE suite group and its
  // drop zone only exists while the panel is empty, so EVERY suite row
  // drag-locks once a group is staged — swapping suites goes
  // unstage-first.
  const staged = isTest ? stagedSuite !== null : roleOf(id) !== null;
  const isDraggable = explorerRowDraggable(
    item,
    { isSchema, isTest, staged, editsLocked },
    { view: studioView, tab: stageTab },
    sources,
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || !isDraggable) return;
    return draggable({
      element,
      getInitialData: (): ExplorerResourceDragData => ({
        kind: EXPLORER_RESOURCE_DRAG_KIND,
        id,
        name,
        icon,
        provider,
        isSchema,
        isTest,
      }),
      onGenerateDragPreview: ({ nativeSetDragImage }) =>
        setDragPreviewChip({ nativeSetDragImage, theme, name }),
      onDragStart: () => setDragging(true),
      onDrop: () => setDragging(false),
    });
  }, [isDraggable, isSchema, isTest, id, name, icon, provider, theme]);

  return { ref, dragging, isDraggable };
}

// No preview may float while an explorer drag is in flight — whichever
// row's card is up when the drag starts closes, and no card opens until
// the drag ends (drop or cancel). The pointer listeners feed the
// press-and-hold guard (previewMenuPin.primaryButtonHeld): a press is the
// start of a drag gesture, so a hover delay that elapses under a held
// button opens nothing.
function usePreviewDragGuard() {
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (e.button === 0) setPrimaryButtonHeld(true);
    };
    const release = () => setPrimaryButtonHeld(false);
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("pointerup", release, true);
    window.addEventListener("pointercancel", release, true);
    const stopMonitor = monitorForElements({
      canMonitor: ({ source }) => isExplorerResourceDragData(source.data),
      onDragStart: () => {
        closeOpenPreview();
        acquirePreviewSuppression();
      },
      onDrop: () => {
        releasePreviewSuppression();
        setPrimaryButtonHeld(false);
      },
    });
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("pointerup", release, true);
      window.removeEventListener("pointercancel", release, true);
      stopMonitor();
      setPrimaryButtonHeld(false);
    };
  }, []);
}

// Figma Bridge_Explorer (13123-100623): rows staged in the pipeline carry a
// role badge — SRC (yellow), MW (orange) or SINK (turquoise), the same StageTag
// chips the Pipelines list uses.
const ROLE_VARIANTS = {
  source: "src",
  transform: "mw",
  sink: "sink",
} as const;

// inline = the badge sits in the row's flow right after the count badge
// (the staged suite row, TTTL-214); otherwise it drifts to the trailing
// edge (the schema rows, which carry no count).
function InUseBadge({ testId, inline }: { testId: string; inline?: boolean }) {
  const badge = (
    <GenericBadgeV2
      content="in use"
      color="primary"
      emphasis="subtle"
      testId={testId}
    />
  );
  return inline ? badge : <InUseBadgeSlot>{badge}</InUseBadgeSlot>;
}

function StagedRoleBadge({ item }: { item: ExplorerItem }) {
  const { roleOf, stagedSuite } = usePipelineStaging();
  // Suite rows: the panel stages one suite group, so exactly the staged
  // suite's row is "in use" (Figma keeps just the count badge on the
  // others) — same green badge as the schemas', but sitting right next
  // to the count badge instead of the row's trailing edge.
  if (isSuiteRowId(item.id)) {
    return stagedSuite !== null &&
      parseSuiteRowId(item.id) === stagedSuite.suiteId ? (
      <InUseBadge testId="suite-in-use-badge" inline />
    ) : null;
  }
  const role = roleOf(item.id);
  if (!role) return null;
  if (!(role in ROLE_VARIANTS)) {
    // A staged schema has no stage position, so instead of a role chip its
    // row carries the green "in use" badge at the row's right edge.
    if (role !== "schema") return null;
    return <InUseBadge testId="schema-in-use-badge" />;
  }
  return (
    <StageTag variant={ROLE_VARIANTS[role as keyof typeof ROLE_VARIANTS]} />
  );
}

// Pushes the "in use" badge to the row's trailing edge — the design keeps
// role chips glued to the name but right-aligns the schema usage badge.
const InUseBadgeSlot = styled.span`
  margin-left: auto;
  display: inline-flex;
  flex-shrink: 0;
`;

// Shared activation semantics for clickable rows rendered as divs: click,
// keyboard focus and Enter/Space all trigger the same action.
function rowActivationProps(active: boolean, action: () => void) {
  if (!active) return {};
  return {
    role: "button" as const,
    tabIndex: 0,
    onClick: action,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      action();
    },
  };
}

// Trailing slot of a tree row: count badge, its skeleton, or a retry action.
// Retry clicks must not bubble into the row's expand/collapse toggle.
function TreeItemTrailing({ item }: { item: ExplorerItem }) {
  return (
    <>
      {item.count != null ? (
        <BadgeSecondarySubtle>{item.count}</BadgeSecondarySubtle>
      ) : (
        item.childrenLoading && <SkeletonBadge />
      )}
      {item.onRetry && (
        <RetryAction
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <ButtonSmallSecondary text="Retry" onClick={item.onRetry} />
        </RetryAction>
      )}
    </>
  );
}

function TreeItemChevron({
  expandable,
  isExpanded,
}: {
  expandable: boolean;
  isExpanded: boolean;
}) {
  const theme = useTheme();
  if (!expandable) return <ChevronSpacer />;
  return (
    <ChevronCell $expanded={isExpanded}>
      <IconSVG
        icon={Icon.Chevron}
        size={12}
        colors={{ default: theme.sidebarListItemChevron }}
      />
    </ChevronCell>
  );
}

// What a leaf's long-hover card can show — schemas, scripts, the
// sample-capable collections and stored tests, on every tab. Clicking a
// leaf does NOTHING: tree content reaches the middle editor only by
// dragging/staging; looking happens in the floating preview card.
function previewKindFor(
  item: ExplorerItem,
  expandable: boolean,
): PreviewKind | null {
  if (expandable) return null;
  if (item.id.startsWith("schema:")) return "schema";
  if (isSuiteRowId(item.id)) return "test";
  if (!item.id.startsWith("resource:")) return null;
  if (canPreviewScript(item.provider)) return "script";
  if (canPreviewSample(item.provider)) return "sample";
  return null;
}

// The row label: an optional dimmed prefix (e.g. the schema's service)
// ahead of the name.
function TreeItemName({ item }: { item: ExplorerItem }) {
  if (item.labelPrefix == null) return <ItemLabel>{item.name}</ItemLabel>;
  return (
    <ItemLabel>
      <LabelPrefix>{item.labelPrefix}</LabelPrefix>
      {` - ${item.name}`}
    </ItemLabel>
  );
}

const rowIndent = (depth: number) =>
  depth === 0 ? ITEM.paddingw : ITEM.indent1;

// Enter or Space on a leaf that could be dragged does what its drop would
// (explorerKeyboard): the resource joins the open tab's stage, a schema
// waits in the tab's bottom block, a suite lands on the Tests tab. The
// source drop's follow-up rides along — the staged source takes the slot
// and the default selection, so an editor-row selection is stale.
function useExplorerKeyboardStage(
  item: ExplorerItem,
  isDraggable: boolean,
): (() => void) | null {
  const { stage, stageSchema, stageTest, stageTab, deselectIf } =
    usePipelineStaging();
  if (!isDraggable) return null;
  const target = explorerLeafStageTarget(
    { isSchema: item.id.startsWith("schema:"), isTest: isSuiteRowId(item.id) },
    stageTab,
  );
  if (!target) return null;
  const resource = {
    id: item.id,
    name: item.name,
    icon: item.icon,
    provider: item.provider,
  };
  return () => {
    if (target.kind === "test") stageTest(resource);
    else if (target.kind === "schema") stageSchema(resource, target.role);
    else {
      stage(target.role, resource);
      if (target.role === "source") deselectIf(SOURCE_EDITOR_ID);
    }
  };
}

// Expandable rows toggle on click/Enter/Space; every leaf is a tab stop,
// and a leaf with somewhere to land stages on Enter/Space (TTTL-186) —
// the pointer gesture stays the drag.
function treeItemActivation(
  expandable: boolean,
  toggle: () => void,
  stageLeaf: (() => void) | null,
) {
  return expandable
    ? rowActivationProps(true, toggle)
    : leafActivationProps(stageLeaf);
}

// The row whose content the editor is showing gets the Mario-style
// active pill treatment on its icon.
function itemIconColors(theme: DefaultTheme, editorActive: boolean) {
  return editorActive
    ? { default: theme.sidebarListItemIcon.active }
    : theme.sidebarListItemIcon;
}

// Which rows carry a right-click Delete menu: stored suites and schemas.
function rowContextMenuKind(rowId: string): "suite" | "schema" | null {
  if (parseSuiteRowId(rowId)) return "suite";
  if (parseSchemaRowId(rowId)) return "schema";
  return null;
}

function RowContextMenu({
  item,
  areaRef,
}: {
  item: ExplorerItem;
  areaRef: React.RefObject<HTMLDivElement>;
}) {
  const suiteId = parseSuiteRowId(item.id);
  if (suiteId) {
    return (
      <SuiteRowContextMenu
        areaRef={areaRef}
        suiteId={suiteId}
        name={item.name}
      />
    );
  }
  const schema = parseSchemaRowId(item.id);
  if (schema) {
    return (
      <SchemaRowContextMenu
        areaRef={areaRef}
        schema={schema}
        name={item.name}
      />
    );
  }
  return null;
}

type ItemRowModel = Extract<ExplorerRow, { kind: "item" }>;

// One tree row of the virtualized list: the item at its depth, expanded
// or not — its children are rows of their own (explorerRows).
function TreeItem({
  row,
  onToggle,
}: {
  row: ItemRowModel;
  onToggle: (id: string, shown: boolean) => void;
}) {
  const { item, depth, expandable, expanded: isExpanded } = row;
  const theme = useTheme();
  const { ref: dragRef, dragging, isDraggable } = useExplorerResourceDrag(item);
  const { editorSourceId } = usePipelineStaging();
  const stageLeaf = useExplorerKeyboardStage(item, isDraggable);
  // Suite and schema rows carry a right-click Delete menu over the row
  // area (the whole row height is the target).
  const hasRowMenu = rowContextMenuKind(item.id) !== null;
  const rowMenuRef = useRef<HTMLDivElement>(null);
  // Long-hovering a previewable leaf floats its content next to the row
  // (Figma Bridge_Explorer 14662-134737 / Bridge_DocPreview): schema
  // definitions, script sources and collection sample documents.
  const previewKind = previewKindFor(item, expandable);
  const hoverPreview = useResourceHoverPreview(
    previewKind ? item : null,
    previewKind,
    dragRef,
    dragging,
  );
  const activation = treeItemActivation(
    expandable,
    () => onToggle(item.id, isExpanded),
    stageLeaf,
  );

  return (
    <RowArea
      ref={rowMenuRef}
      // The right-click that opens a row's Delete menu closes an open
      // hover preview right away, and holds ALL tree previews off for
      // the menu's lifetime — no card may pop over it from other rows.
      onContextMenu={
        hasRowMenu
          ? () => {
              hoverPreview.close();
              suppressPreviewsWhileMenuOpen();
            }
          : undefined
      }
    >
      <ItemRow
        ref={dragRef}
        $indent={rowIndent(depth)}
        $dragging={dragging}
        $draggable={isDraggable}
        $active={editorSourceId === item.id}
        {...hoverPreview.getReferenceProps(activation)}
      >
        <TreeItemChevron expandable={expandable} isExpanded={isExpanded} />
        <IconSVG
          icon={item.icon}
          size={ITEM.icon}
          colors={itemIconColors(theme, editorSourceId === item.id)}
        />
        <TreeItemName item={item} />
        {/* Count before the staged badge: the count badge stays glued
            to the name on every row (rows with role chips carry no
            count), while "in use" alone drifts to the trailing edge. */}
        <TreeItemTrailing item={item} />
        <StagedRoleBadge item={item} />
      </ItemRow>
      <RowContextMenu item={item} areaRef={rowMenuRef} />
      {hoverPreview.preview}
    </RowArea>
  );
}

function SectionHeaderRow({
  section,
  sectionCollapsed,
  onToggle,
}: {
  section: ExplorerSection;
  sectionCollapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <SectionSticky>
      <SectionHeader
        {...rowActivationProps(true, onToggle)}
        title={sectionCollapsed ? "Expand section" : "Collapse section"}
      >
        <SectionLabel>{section.label}</SectionLabel>
        {section.count != null ? (
          <BadgeSecondarySubtle>{section.count}</BadgeSecondarySubtle>
        ) : (
          section.loading && <SkeletonBadge />
        )}
        <SectionDivider />
        {/* Decorative state indicator — the whole header is the control. */}
        <SectionMicroButton
          tabIndex={-1}
          aria-hidden
          icon={
            sectionCollapsed ? <ExpandSectionIcon /> : <CollapseSectionIcon />
          }
        />
      </SectionHeader>
    </SectionSticky>
  );
}

function ExplorerRowView({
  row,
  onToggleSection,
  onToggleItem,
}: {
  row: ExplorerRow;
  onToggleSection: (id: string) => void;
  onToggleItem: (id: string, shown: boolean) => void;
}) {
  switch (row.kind) {
    case "section":
      return (
        <SectionHeaderRow
          section={row.section}
          sectionCollapsed={row.collapsed}
          onToggle={() => onToggleSection(row.section.id)}
        />
      );
    case "item":
      return <TreeItem row={row} onToggle={onToggleItem} />;
    case "skeleton":
      return (
        <SkeletonTreeRows
          rows={1}
          indent={row.depth === 0 ? ITEM.paddingw : ITEM.indent1}
        />
      );
  }
}

type Pin = "section" | "item" | null;

// The rows pinned at the top for the current scroll position: the section
// header above the first visible row (top 0), and the expanded top-level
// item whose subtree is at the top (one row below it). Every row is one
// row height, so the row sitting one height down is startIndex + 1.
function pinsFor(
  rows: readonly ExplorerRow[],
  startIndex: number,
): { section: number | null; item: number | null } {
  return {
    section: stickyRowsAt(rows, startIndex).section,
    item: stickyRowsAt(rows, startIndex + 1).item,
  };
}

// The tree, virtualized: only the rows in and around the viewport are in
// the DOM (a MongoDB service alone can list hundreds of collections). The
// pinned headers ride along through the range extractor and render
// sticky, in flow at the top of the list box, so they stay put while the
// absolutely placed rows scroll under them (mario's ConnectionTree pins,
// kept from the nested layout this replaced).
function ExplorerTree({
  rows,
  onToggleSection,
  onToggleItem,
  children,
}: {
  rows: ExplorerRow[];
  onToggleSection: (id: string) => void;
  onToggleItem: (id: string, shown: boolean) => void;
  /** Rendered above the rows (loading skeletons, the empty hint). */
  children?: React.ReactNode;
}) {
  const scrollRef = useRef<OverlayScrollbarsComponentRef>(null);
  const [viewport, setViewport] = useState<HTMLElement | null>(null);
  // The scroll element is OverlayScrollbars' React-rendered viewport —
  // there from the first render, so the rows never wait on the
  // (deferred) scrollbar init.
  useLayoutEffect(() => {
    setViewport(
      scrollRef.current
        ?.getElement()
        ?.querySelector<HTMLElement>("[data-overlayscrollbars-contents]") ??
        null,
    );
  }, []);

  const withPins = useCallback(
    (range: Range) => {
      const pins = pinsFor(rows, range.startIndex);
      const pinned = [pins.section, pins.item].filter(
        (i): i is number => i !== null,
      );
      return [...new Set([...pinned, ...defaultRangeExtractor(range)])];
    },
    [rows],
  );
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => viewport,
    estimateSize: () => ITEM.height,
    overscan: 8,
    getItemKey: (index) => rows[index].key,
    rangeExtractor: withPins,
  });
  const pins = pinsFor(rows, virtualizer.range?.startIndex ?? 0);
  const pinOf = (index: number): Pin =>
    index === pins.section ? "section" : index === pins.item ? "item" : null;

  return (
    <ScrollArea ref={scrollRef}>
      {children}
      <VirtualList style={{ height: virtualizer.getTotalSize() }}>
        {virtualizer.getVirtualItems().map((v) => {
          const pin = pinOf(v.index);
          return (
            <VirtualRow
              key={v.key}
              $pin={pin}
              style={
                pin ? undefined : { transform: `translateY(${v.start}px)` }
              }
            >
              <ExplorerRowView
                row={rows[v.index]}
                onToggleSection={onToggleSection}
                onToggleItem={onToggleItem}
              />
            </VirtualRow>
          );
        })}
      </VirtualList>
    </ScrollArea>
  );
}

// Figma: Content panel (13140-85610) — collapsed strip, mirror of the AI
// Helper's but anchored on the left: expand button on top, rotated label.
function CollapsedExplorer({ onExpand }: { onExpand: () => void }) {
  return (
    <CollapsedPanel
      onClick={onExpand}
      title="Expand Explorer"
      data-testid="explorer-expand"
    >
      <CollapsedExpandIcon>
        <IconSVG
          icon={Icon.CollapseRight}
          size={16}
          svgColorKey={SVGColorKey.semanticTextBody}
        />
      </CollapsedExpandIcon>
      <CollapsedLabel>Explorer</CollapsedLabel>
    </CollapsedPanel>
  );
}

function ExplorerFilterInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <FilterRow>
      <GenericInput
        mode={InputMode.Control}
        placeholder="Filter"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        icon={{
          variant: Icon.Filter,
          size: 16,
          position: InputIconPosition.Leading,
        }}
        allowClear={value.length > 0}
        onClear={() => onChange("")}
      />
    </FilterRow>
  );
}

export function NewExplorer() {
  const ui = useExplorer();
  const { sections, servicesLoading, hasServices } = ui;
  const { studioView, stageTab } = usePipelineStaging();
  const queryClient = useQueryClient();
  const onTreeScroll = usePreviewScrollGuard();
  usePreviewDragGuard();

  // Each tab shows only the sections whose rows can land on it — the
  // per-tab rules (and the matching empty hints) live in
  // explorerTabSections.ts.
  const target = { view: studioView, tab: stageTab };
  const filter = ui.filterText.trim().toLowerCase();
  const visibleSections = visibleExplorerSections(sections, target, filter);
  const emptyHint = explorerEmptyHint(target, sections, {
    servicesLoading,
    hasServices,
  });
  const { expanded, filterOverrides, collapsedSections } = ui;
  const rows = useMemo(
    () =>
      servicesLoading
        ? []
        : flattenExplorerRows(visibleSections, {
            filter,
            expanded,
            filterOverrides,
            collapsedSections,
          }),
    [
      servicesLoading,
      visibleSections,
      filter,
      expanded,
      filterOverrides,
      collapsedSections,
    ],
  );

  if (ui.collapsed) {
    return <CollapsedExplorer onExpand={() => ui.setCollapsed(false)} />;
  }

  return (
    // No browser context menu over the tree — there's nothing sensible on
    // it, and it would sit on top of the schema hover previews.
    // Scrolling anywhere in the panel (only the tree scrolls) pauses the
    // hover previews and closes an open card.
    <Panel
      onContextMenu={(e) => e.preventDefault()}
      onScrollCapture={onTreeScroll}
    >
      <Header>
        <HeaderLabel>Explorer</HeaderLabel>
        <HeaderActions>
          <ButtonSmallSubtleIcon
            icon={Icon.Refresh}
            onClick={() => void refreshExplorer(queryClient)}
            testId="explorer-refresh"
          />
          <ButtonSmallSubtleIcon
            icon={Icon.CollapseLeft}
            onClick={() => ui.setCollapsed(true)}
            testId="explorer-collapse"
          />
        </HeaderActions>
      </Header>
      <ExplorerFilterInput value={ui.filterText} onChange={ui.setFilterText} />
      <ExplorerTree
        rows={rows}
        onToggleSection={ui.toggleSection}
        onToggleItem={ui.toggleItem}
      >
        {servicesLoading && (
          <SkeletonTreeRows rows={8} indent={ITEM.paddingw} />
        )}
        {emptyHint && <EmptyHint>{emptyHint}</EmptyHint>}
      </ExplorerTree>
    </Panel>
  );
}

// Figma: card with left-rounded corners, filling the row's resizable
// Explorer panel (330 at rest, up to 520 — SidePanel in index.tsx); the
// workbench's border is the shared right edge.
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: ${(p) => p.theme.sidebarBackground};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-right: none;
  border-radius: ${CARDS.corner}px 0 0 ${CARDS.corner}px;
  padding: 0 ${SIDEBAR.padding}px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  padding-left: ${ITEM.paddingw}px;
  flex-shrink: 0;
`;

const HeaderLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
`;

// Figma Sidebar/header Actions group: refresh + collapse.
const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${SIDEBAR.header.gap}px;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  height: 50px;
  padding: 0 ${SEMANTIC.spacings}px;
  flex-shrink: 0;
`;

const ScrollArea = styled(OverlayScroll)`
  flex: 1;
  min-height: 0;
  padding: 0 ${SIDEBAR.list.item.padding}px;
`;

// The virtualized list box: as tall as every row together, the rendered
// rows placed inside it by transform.
const VirtualList = styled.div`
  position: relative;
  width: 100%;
`;

// A rendered row. Pinned rows (the section header above the viewport, the
// expanded top-level item whose children are scrolling) sit in flow at
// the top of the box and stick — the section at 0, the item one row
// below — over the rows sliding under them; their solid background hides
// those rows. The rest are absolutely placed at their offsets.
const VirtualRow = styled.div<{ $pin: Pin }>`
  position: ${(p) => (p.$pin ? "sticky" : "absolute")};
  top: ${(p) => (p.$pin === "item" ? ITEM.height : 0)}px;
  left: 0;
  width: 100%;
  z-index: ${(p) => (p.$pin === "section" ? 3 : p.$pin === "item" ? 2 : "auto")};
  background: ${(p) => (p.$pin ? p.theme.sidebarBackground : "transparent")};
`;

const SectionSticky = styled.div``;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: ${ITEM.height}px;
  padding: 0 ${SEMANTIC.spacings}px;
  flex-shrink: 0;
  cursor: pointer;
  border-radius: ${ITEM.corner}px;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;

// The row's context-menu area (the whole row height is the target).
const RowArea = styled.div``;

const SectionLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${(p) => p.theme.semanticTextLight};
`;

const SectionDivider = styled.span`
  flex: 1;
  min-width: 1px;
  height: 1px;
  background: ${(p) => p.theme.semanticBorderLight};
`;

const SectionMicroButton = styled(MicroButton)`
  color: ${(p) => p.theme.sidebarListItemText};
`;

const RetryAction = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

// Active (editor is showing this resource's sample) = the Mario connection
// tree pill: the rounded row keeps the green active background. While the
// row is being dragged it holds the gray hover pill at 50% opacity (Figma
// Bridge_Explorer drag state 14397-86458).
const ItemRow = styled.div<{
  $indent: number;
  $dragging?: boolean;
  $draggable?: boolean;
  $active?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: ${ITEM.gap}px;
  height: ${ITEM.height}px;
  padding-left: ${(p) => p.$indent}px;
  padding-right: ${ITEM.paddingw}px;
  flex-shrink: 0;
  /* Draggable rows advertise grab on hover; from mousedown on, the browser's
     native drag cursor behavior takes over. */
  cursor: ${(p) => (p.$draggable ? "grab" : p.onClick ? "pointer" : "default")};
  border-radius: ${ITEM.corner}px;
  opacity: ${(p) => (p.$dragging ? 0.5 : 1)};
  background: ${(p) =>
    p.$active
      ? p.theme.sidebarListItemActiveBackground
      : p.$dragging
        ? p.theme.sidebarListItemHoverBackground
        : "transparent"};

  &:hover {
    background: ${(p) =>
      p.$active
        ? p.theme.sidebarListItemActiveBackground
        : p.theme.sidebarListItemHoverBackground};
  }
`;

const ChevronCell = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${ITEM.icon}px;
  height: ${ITEM.icon}px;
  flex-shrink: 0;
  transition: transform 0.15s ease;
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
`;

const ChevronSpacer = styled.span`
  width: ${ITEM.icon}px;
  flex-shrink: 0;
`;

const ItemLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.2;
  color: ${(p) => p.theme.sidebarListItemText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// The semibold encoding prefix on schema leaves — "JSON - name" per Figma
// Bridge_Explorer (14397-190443, Inter Semi Bold).
const LabelPrefix = styled.span`
  font-weight: 600;
`;

// Count-badge placeholder matching BadgeSecondarySubtle's footprint
// (20x20 rounded square for a single digit).
const SkeletonBadge = styled(SkeletonBar)`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  flex-shrink: 0;
`;

// Same box as ItemRow so skeletons don't shift the layout when data lands.
const SkeletonRow = styled.div<{ $indent: number }>`
  display: flex;
  align-items: center;
  gap: ${ITEM.gap}px;
  height: ${ITEM.height}px;
  padding-left: ${(p) => p.$indent}px;
  padding-right: ${ITEM.paddingw}px;
  flex-shrink: 0;
`;

const EmptyHint = styled.div`
  display: flex;
  align-items: center;
  height: ${ITEM.height}px;
  padding: 0 ${ITEM.paddingw}px;
  font-size: 14px;
  color: ${(p) => p.theme.semanticTextLight};
`;

// Figma: Content panel (13140-85610) — collapsed strip, mirror of the AI
// Helper's but anchored on the left: expand button on top, rotated label.
// Fills the collapsed SidePanel (44px wide).
const CollapsedPanel = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${ITEM.gap}px;
  padding: ${SEMANTIC.spacingm}px 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: ${(p) => p.theme.sidebarBackground};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-right: none;
  border-radius: ${CARDS.corner}px 0 0 ${CARDS.corner}px;
  cursor: pointer;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;

const CollapsedExpandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
`;

const CollapsedLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;
